// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, AlertCircle, ChevronDown, Zap } from 'lucide-react';
import budgetPeriodAPI from '../api/services/budget/budgetPeriodService';
import testDataAPI from '../api/services/testDataService';
import BudgetPeriodForm from '../components/budget/BudgetPeriodForm';
import AllocationForm from '../components/budget/AllocationForm';

export default function BudgetPeriods() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [expandedPeriod, setExpandedPeriod] = useState(null);
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [testDataLoading, setTestDataLoading] = useState(false);
  const [testDataMessage, setTestDataMessage] = useState('');

  useEffect(() => {
    loadPeriods();
  }, []);

  const loadPeriods = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await budgetPeriodAPI.list();
      setPeriods(res.data || []);
    } catch (err) {
      setError('Failed to load budget periods');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePopulateTestData = async () => {
    setTestDataLoading(true);
    setTestDataMessage('');
    try {
      const result = await testDataAPI.populateTestCategories();
      setTestDataMessage(result.message);

      if (result.created.length > 0) {
        // Show success message and clear after 5 seconds
        setTimeout(() => setTestDataMessage(''), 5000);
      }
    } catch (err) {
      setTestDataMessage('Failed to populate test data');
      console.error(err);
    } finally {
      setTestDataLoading(false);
    }
  };

  const handleCreatePeriod = async (data) => {
    try {
      const res = await budgetPeriodAPI.create(data);
      setPeriods([res.data, ...periods]);
      setShowForm(false);
      setEditingPeriod(null);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to create budget period';
      setError(detail);
      console.error('Create period error:', err);
    }
  };

  const handleUpdatePeriod = async (data) => {
    try {
      const res = await budgetPeriodAPI.update(editingPeriod.id, data);
      setPeriods(
        periods.map((p) => (p.id === editingPeriod.id ? res.data : p)),
      );
      setShowForm(false);
      setEditingPeriod(null);
    } catch (err) {
      setError(
        err.response?.data?.detail || 'Failed to update budget period',
      );
    }
  };

  const handleDeletePeriod = async (id) => {
    if (!confirm('Delete this budget period?')) return;
    try {
      await budgetPeriodAPI.delete(id);
      setPeriods(periods.filter((p) => p.id !== id));
    } catch (err) {
      setError('Failed to delete budget period');
    }
  };

  const handleCreateAllocation = async (data) => {
    try {
      const res = await budgetPeriodAPI.createAllocation(
        expandedPeriod.id,
        data,
      );
      setPeriods(
        periods.map((p) =>
          p.id === expandedPeriod.id
            ? { ...p, allocations: [...(p.allocations || []), res.data] }
            : p,
        ),
      );
      setShowAllocationForm(false);
      setEditingAllocation(null);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to create allocation';
      setError(detail);
      console.error('Create allocation error:', err);
    }
  };

  const handleUpdateAllocation = async (data) => {
    try {
      const res = await budgetPeriodAPI.updateAllocation(
        expandedPeriod.id,
        editingAllocation.id,
        data,
      );
      setPeriods(
        periods.map((p) =>
          p.id === expandedPeriod.id
            ? {
                ...p,
                allocations: (p.allocations || []).map((a) =>
                  a.id === editingAllocation.id ? res.data : a,
                ),
              }
            : p,
        ),
      );
      setShowAllocationForm(false);
      setEditingAllocation(null);
    } catch (err) {
      setError('Failed to update allocation');
    }
  };

  const handleDeleteAllocation = async (allocationId) => {
    if (!confirm('Delete this allocation?')) return;
    try {
      await budgetPeriodAPI.deleteAllocation(expandedPeriod.id, allocationId);
      setPeriods(
        periods.map((p) =>
          p.id === expandedPeriod.id
            ? {
                ...p,
                allocations: (p.allocations || []).filter(
                  (a) => a.id !== allocationId,
                ),
              }
            : p,
        ),
      );
    } catch (err) {
      setError('Failed to delete allocation');
    }
  };

  const currentPeriod = periods.find((p) => p.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading budget periods…</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Budget Periods</h1>
        <div className="flex gap-2">
          {/* Dev Test Button */}
          <button
            onClick={handlePopulateTestData}
            disabled={testDataLoading}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed"
            title="[DEV] Populate test categories and subcategories"
          >
            <Zap size={16} />
            {testDataLoading ? 'Loading...' : 'Test Data'}
          </button>
          <button
            onClick={() => {
              setEditingPeriod(null);
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus size={18} />
            New Period
          </button>
        </div>
      </div>

      {/* Test Data Message */}
      {testDataMessage && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm flex items-center justify-between">
          <span>{testDataMessage}</span>
          <button
            onClick={() => setTestDataMessage('')}
            className="text-yellow-600 hover:text-yellow-800 font-medium"
          >
            ✕
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full max-h-[85vh] overflow-y-auto border-2 border-indigo-500">
            <BudgetPeriodForm
              initial={editingPeriod}
              onSubmit={editingPeriod ? handleUpdatePeriod : handleCreatePeriod}
              onClose={() => {
                setShowForm(false);
                setEditingPeriod(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Allocation Form Modal */}
      {showAllocationForm && expandedPeriod && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full max-h-[85vh] overflow-y-auto border-2 border-indigo-500">
            <AllocationForm
              periodId={expandedPeriod.id}
              initial={editingAllocation}
              periodAllocations={expandedPeriod.allocations}
              onSubmit={
                editingAllocation
                  ? handleUpdateAllocation
                  : handleCreateAllocation
              }
              onClose={() => {
                setShowAllocationForm(false);
                setEditingAllocation(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Current Period Highlight */}
      {currentPeriod && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-indigo-900 mb-3">
            Active Period: {currentPeriod.name}
          </h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-indigo-600 text-xs font-medium">Total Allocated</p>
              <p className="text-lg font-bold text-indigo-900">
                ₹{currentPeriod.total_allocated?.toLocaleString() || 0}
              </p>
            </div>
            <div>
              <p className="text-indigo-600 text-xs font-medium">Total Spent</p>
              <p className="text-lg font-bold text-indigo-900">
                ₹{currentPeriod.total_spent?.toLocaleString() || 0}
              </p>
            </div>
            <div>
              <p className="text-indigo-600 text-xs font-medium">Remaining</p>
              <p className="text-lg font-bold text-indigo-900">
                ₹
                {(
                  (currentPeriod.total_allocated || 0) -
                  (currentPeriod.total_spent || 0)
                ).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Periods List */}
      <div className="space-y-3">
        {periods.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No budget periods yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Create one to start zero-based budgeting
            </p>
          </div>
        ) : (
          periods.map((period) => (
            <div key={period.id} className="border border-gray-200 rounded-lg">
              {/* Period Header */}
              <button
                onClick={() =>
                  setExpandedPeriod(
                    expandedPeriod?.id === period.id ? null : period,
                  )
                }
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <ChevronDown
                    size={18}
                    className={`text-gray-400 transition-transform ${
                      expandedPeriod?.id === period.id ? 'rotate-180' : ''
                    }`}
                  />
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">
                      {period.name}
                      {period.is_active && (
                        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          Active
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {(period.allocations || []).length} allocation{(period.allocations || []).length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    ₹{period.total_allocated?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-gray-500">allocated</p>
                </div>
              </button>

              {/* Expanded Content */}
              {expandedPeriod?.id === period.id && (
                <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-4">
                  {/* Period Actions */}
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => {
                        setEditingPeriod(period);
                        setShowForm(true);
                      }}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeletePeriod(period.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Allocations List */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Allocations</h4>
                      <button
                        onClick={() => {
                          setEditingAllocation(null);
                          setShowAllocationForm(true);
                        }}
                        className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        + Add
                      </button>
                    </div>
                    {(period.allocations || []).length === 0 ? (
                      <p className="text-xs text-gray-500 py-4 text-center">
                        No allocations yet
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {(period.allocations || []).map((alloc) => (
                          <div
                            key={alloc.id}
                            className="bg-white p-3 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {alloc.category_name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Allocated: ₹
                                  {alloc.allocated_amount?.toLocaleString() || 0}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-gray-900">
                                  {alloc.utilization_percentage}%
                                </p>
                                <div className="w-24 h-2 bg-gray-200 rounded-full mt-1">
                                  <div
                                    className={`h-full rounded-full ${
                                      (alloc.utilization_percentage || 0) > 100
                                        ? 'bg-red-500'
                                        : 'bg-green-500'
                                    }`}
                                    style={{
                                      width: `${Math.min(alloc.utilization_percentage || 0, 100)}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => {
                                  setEditingAllocation(alloc);
                                  setShowAllocationForm(true);
                                }}
                                className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteAllocation(alloc.id)
                                }
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Period Stats */}
                  <div className="bg-white p-3 rounded-lg border border-gray-200 grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-xs text-gray-500">Total Allocated</p>
                      <p className="font-semibold text-gray-900">
                        ₹{period.total_allocated?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Spent</p>
                      <p className="font-semibold text-gray-900">
                        ₹{period.total_spent?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Remaining</p>
                      <p className="font-semibold text-gray-900">
                        ₹
                        {(
                          (period.total_allocated || 0) -
                          (period.total_spent || 0)
                        ).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
